import type { PipeIntegrationDef, PipeIntegration } from '@render/integrations'
import type { PipeInterface } from '@render/composables/usePipe'
import { watch } from 'vue'
import { loadPrimerSdk, PRIMER_CDN_BASE } from '@render/integrations/primer/primerSdk'

const CDN_HOSTS = ['https://sdk.primer.io', 'https://api.primer.io']

// Primer pre-warm integration — warms CDN connections, pre-caches the checkout
// stylesheet, and loads the SDK npm chunk before the user reaches the checkout
// page. The Primer class is bundled via the npm package; CDN script injection
// only happens as loadPrimerSdk's fallback when the chunk import fails.
// Auto-activated at build time when PrimerCheckout is used in the pipe (see
// builder/lib/pages.ts getActiveIntegrations).
const primer: PipeIntegration = (): PipeIntegrationDef => {
  return {
    setup(pipe: PipeInterface) {
      if (typeof window === 'undefined') return

      // Warm TCP/TLS connections to Primer's CDN immediately on setup —
      // before any user interaction.
      for (const href of CDN_HOSTS) {
        const dns = document.createElement('link')
        dns.rel = 'dns-prefetch'
        dns.href = href
        document.head.appendChild(dns)

        const preconnect = document.createElement('link')
        preconnect.rel = 'preconnect'
        preconnect.href = href
        document.head.appendChild(preconnect)
      }

      // rel=preload warming. A failed preload is harmless for correctness
      // (the browser simply fetches at checkout time instead), but the dead
      // link is removed on error and the flag reset so a later loadAssets run
      // (online retry, email change) re-warms with a fresh element.
      let preloadInjected = false
      // The stylesheet is different: a FAILED Checkout.css <link> must never
      // stay in the DOM. Primer's loader dedupes by element — querySelector
      // on the href, then waits on that element's onload/onerror — and a dead
      // link fires neither again, hanging showUniversalCheckout forever. The
      // link removes itself on error and resets this flag so a later
      // loadAssets run injects a fresh one.
      let cssInjected = false
      // The SDK chunk load DOES retry. An offline visitor fails it right at
      // the email step (this watcher's trigger); without a retry the checkout
      // would later mount against a dead loader. loadPrimerSdk never caches a
      // rejection, so re-running loadAssets re-attempts the load.
      let sdkLoaded = false
      let onlineRetryArmed = false

      const preload = (href: string, as: string) => {
        const link = document.createElement('link')
        link.rel = 'preload'
        link.as = as
        link.href = href
        link.crossOrigin = 'anonymous'
        link.onerror = () => {
          link.remove()
          preloadInjected = false
        }
        document.head.appendChild(link)
      }

      const loadAssets = () => {
        if (!preloadInjected) {
          preloadInjected = true

          // Preload the core SDK bundle fetched by showUniversalCheckout — browser
          // downloads and caches it without executing.
          preload(`${PRIMER_CDN_BASE}/PrimerCoreSDK.min.js`, 'script')
        }

        if (!cssInjected) {
          cssInjected = true

          // Pre-cache the checkout stylesheet. Primer's CSS is scoped to its own
          // #primer-checkout-* selectors so applying it early is safe.
          const css = document.createElement('link')
          css.rel = 'stylesheet'
          css.href = `${PRIMER_CDN_BASE}/Checkout.css`
          css.onerror = () => {
            css.remove()
            cssInjected = false
          }
          document.head.appendChild(css)
        }

        if (sdkLoaded) return

        // Resolve the SDK npm chunk now so it is already in module cache when
        // PrimerCheckout mounts and calls loadPrimerSdk() itself.
        loadPrimerSdk()
          .then(() => {
            sdkLoaded = true
          })
          .catch((err) => {
            // Offline (or a CDN hiccup) during the pre-warm. loadPrimerSdk has
            // already cleared its singleton, so retry the moment the browser
            // reports connectivity back. The checkout mount retries on its own
            // too — this just front-loads the recovery.
            console.warn('[primer] SDK pre-warm failed — will retry when back online', err)
            if (onlineRetryArmed) return
            onlineRetryArmed = true
            window.addEventListener(
              'online',
              () => {
                onlineRetryArmed = false
                loadAssets()
              },
              { once: true },
            )
          })
      }

      // Trigger once email is known — user is past intake, heading to checkout.
      // Fires immediately for returning users with email in persisted state.
      watch(
        () => pipe.getData('var.global.email'),
        (email) => {
          if (typeof email === 'string' && email.trim().length > 0) loadAssets()
        },
        { immediate: true },
      )
    },
  }
}

export default primer
